import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { useI18n } from "../../i18n";
import { CHART_COLORS, formatChartValue, layoutChart, type ChartSpec } from "../../lib/chart";

/** Width of an element, following resizes (jsdom and very old browsers: the fallback). */
function useWidth<T extends HTMLElement>(fallback: number): [RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => el.clientWidth > 0 && setW(el.clientWidth);
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const fill = (c: keyof typeof CHART_COLORS) => ({ fill: `var(${CHART_COLORS[c].token})` });

/**
 * Stacked bar chart (UI phase D, ADR 0031): SVG drawn from the same layout as the PDF chart. Hover, tap or
 * the arrow keys show one bar's values; the chart is one tab stop. The text alternative is the summary
 * sentence (the SVG's name) and the chart's data as a table behind a disclosure.
 */
export function BarChart({ spec, label }: { spec: ChartSpec; label: string }) {
  const { t } = useI18n();
  const [ref, width] = useWidth<HTMLDivElement>(640);
  const [active, setActive] = useState<number | null>(null);
  // The data table is built only when asked for (one table per report screen otherwise).
  const [showData, setShowData] = useState(false);
  const lay = useMemo(() => layoutChart(spec, width), [spec, width]);
  const tipId = useId();
  const vertical = spec.orientation === "vertical";
  const multi = spec.series.length > 1;
  const fmt = (v: number) => formatChartValue(spec, v);
  const n = lay.bars.length;
  const cur = active !== null && active < n ? lay.bars[active]! : null;

  const onKey = (e: KeyboardEvent) => {
    if (n === 0) return;
    const next = (d: number) => setActive((a) => (a === null ? (d > 0 ? 0 : n - 1) : Math.min(n - 1, Math.max(0, a + d))));
    if (e.key === "ArrowRight" || e.key === "ArrowDown") (next(1), e.preventDefault());
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") (next(-1), e.preventDefault());
    else if (e.key === "Home") (setActive(0), e.preventDefault());
    else if (e.key === "End") (setActive(n - 1), e.preventDefault());
    else if (e.key === "Escape") setActive(null);
  };

  // Tooltip position: above the bar (vertical) or right of it (horizontal), kept inside the chart.
  const tip = cur
    ? vertical
      ? { left: Math.min(Math.max(cur.band.x + cur.band.w / 2, 100), lay.width - 100), top: Math.max(0, Math.min(...cur.segs.map((s) => s.y))) }
      : { left: Math.min(Math.max(lay.plot.x + 100, (cur.segs.at(-1)?.x ?? 0) + (cur.segs.at(-1)?.w ?? 0)), lay.width - 100), top: cur.band.y }
    : null;

  return (
    <figure className="chart">
      {multi && (
        <ul className="chart-legend">
          {spec.series.map((s) => (
            <li key={s.label}>
              <span className="chart-swatch" aria-hidden="true" style={{ background: `var(${CHART_COLORS[s.color].token})` }} />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div
        ref={ref}
        className="chart-area"
        tabIndex={0}
        role="group"
        aria-roledescription={t("charts.roleDescription")}
        aria-label={`${label}. ${spec.summary}`}
        aria-describedby={cur ? tipId : undefined}
        onKeyDown={onKey}
        onBlur={() => setActive(null)}
        onPointerLeave={(e) => e.pointerType === "mouse" && setActive(null)}
      >
        <svg width={lay.width} height={lay.height} viewBox={`0 0 ${lay.width} ${lay.height}`} aria-hidden="true" className="chart-svg">
          {lay.ticks.map((tk) =>
            vertical ? (
              <g key={tk.value}>
                <line x1={lay.plot.x} x2={lay.plot.x + lay.plot.w} y1={tk.pos} y2={tk.pos} className="chart-grid" />
                <text x={lay.plot.x - 6} y={tk.pos + 4} textAnchor="end" className="chart-tick">
                  {tk.label}
                </text>
              </g>
            ) : (
              <g key={tk.value}>
                <line x1={tk.pos} x2={tk.pos} y1={lay.plot.y} y2={lay.plot.y + lay.plot.h} className="chart-grid" />
                <text x={tk.pos} y={lay.plot.y + lay.plot.h + 16} textAnchor="middle" className="chart-tick">
                  {tk.label}
                </text>
              </g>
            ),
          )}
          {lay.bars.map((b, i) => (
            <g key={b.key} className={active !== null && active !== i ? "chart-bar is-dim" : "chart-bar"}>
              {b.segs.map((s) =>
                s.w > 0 && s.h > 0 ? <rect key={s.series} x={s.x} y={s.y} width={s.w} height={s.h} style={fill(spec.series[s.series]!.color)} className="chart-seg" /> : null,
              )}
              {b.showLabel &&
                (vertical ? (
                  <text x={b.lx} y={b.ly} textAnchor="middle" className="chart-label">
                    {b.label}
                  </text>
                ) : (
                  <text x={b.lx} y={b.ly + 4} textAnchor="end" className="chart-label">
                    {b.label}
                  </text>
                ))}
              <rect
                x={b.band.x}
                y={b.band.y}
                width={b.band.w}
                height={b.band.h}
                className="chart-hit"
                onPointerEnter={(e) => e.pointerType === "mouse" && setActive(i)}
                onClick={() => setActive((a) => (a === i ? null : i))}
              />
            </g>
          ))}
          {vertical ? (
            <line x1={lay.plot.x} x2={lay.plot.x + lay.plot.w} y1={lay.plot.y + lay.plot.h} y2={lay.plot.y + lay.plot.h} className="chart-baseline" />
          ) : (
            <line x1={lay.plot.x} x2={lay.plot.x} y1={lay.plot.y} y2={lay.plot.y + lay.plot.h} className="chart-baseline" />
          )}
        </svg>
        <div id={tipId} className={`chart-tip${cur ? " is-on" : ""}`} role="status" style={tip ? { left: tip.left, top: tip.top } : undefined}>
          {cur && (
            <>
              <strong>{cur.title}</strong>
              {cur.segs.map((s) => (
                <span key={s.series} className="chart-tip-row">
                  {multi && <span className="chart-swatch" aria-hidden="true" style={{ background: `var(${CHART_COLORS[spec.series[s.series]!.color].token})` }} />}
                  {multi && <span className="chart-tip-label">{spec.series[s.series]!.label}: </span>}
                  <span className="num">{fmt(s.value)}</span>
                </span>
              ))}
              {multi && spec.totalLabel && (
                <span className="chart-tip-row chart-tip-total">
                  <span className="chart-tip-label">{spec.totalLabel}: </span>
                  <span className="num">{fmt(cur.total)}</span>
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <details className="chart-data" onToggle={(e) => setShowData((e.currentTarget as HTMLDetailsElement).open)}>
        <summary>{t("charts.showData")}</summary>
        {showData && (
        <div className="table-wrap table-scroll">
          <table className="table report-table">
            <caption className="sr-only">{label}</caption>
            <thead>
              <tr>
                <th scope="col">{spec.categoryLabel ?? t("charts.category")}</th>
                {spec.series.map((s) => (
                  <th key={s.label} scope="col" className="num">
                    {s.label}
                  </th>
                ))}
                {multi && spec.totalLabel && (
                  <th scope="col" className="num">
                    {spec.totalLabel}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {spec.bars.map((b, i) => (
                <tr key={b.key}>
                  <th scope="row">{b.title ?? b.label}</th>
                  {b.values.map((v, s) => (
                    <td key={s} className="num">
                      {fmt(v)}
                    </td>
                  ))}
                  {multi && spec.totalLabel && <td className="num">{fmt(lay.bars[i]?.total ?? 0)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </details>
    </figure>
  );
}
